import { useUnsafeMemo } from 'modules/core/hooks/useMemo'

import type {
  EventLayout,
  Row,
  TimeGranularity,
  TimeGridEntity,
  TimeGridEvent,
  TimeRange,
  TimeSlot,
} from 'modules/shift_management/components/ShiftManagement/TimeGridTable/types'
import { buildEventLayouts } from 'modules/shift_management/utils/TimeGridTable/layout'
import { buildTimeSlots } from 'modules/shift_management/utils/TimeGridTable/slots'
import { stackEvents } from 'modules/shift_management/utils/TimeGridTable/stacking'

interface UseTimeGridLayoutProps<TEntity extends TimeGridEntity, TEvent extends TimeGridEvent> {
  entities: TEntity[]
  events: TEvent[]
  timeRange: TimeRange
  granularity: TimeGranularity
}

// Groups events by entity, dropping those without a matching row.
const groupEventsByEntity = <TEntity extends TimeGridEntity, TEvent extends TimeGridEvent>({
  entities,
  events,
}: {
  entities: TEntity[]
  events: TEvent[]
}): Map<string, TEvent[]> => {
  const entityIds = new Set(entities.map((entity) => entity.id))
  const grouped = new Map<string, TEvent[]>()

  events.forEach((event) => {
    if (!entityIds.has(event.entityId)) {
      return
    }

    const list = grouped.get(event.entityId)
    if (list) {
      list.push(event)
      return
    }
    grouped.set(event.entityId, [event])
  })

  return grouped
}

// Extends day view to show events ending after the configured range.
const computeExtraHours = (events: TimeGridEvent[], timeRange: { start: Date; end: Date }) => {
  const latestEnd = events.reduce((latest, event) => {
    return event.end > latest ? event.end : latest
  }, timeRange.end)

  if (latestEnd <= timeRange.end) return 0

  const diffInMs = latestEnd.getTime() - timeRange.end.getTime()
  return Math.ceil(diffInMs / (60 * 60 * 1000))
}

function useTimeGridLayout<TEntity extends TimeGridEntity, TEvent extends TimeGridEvent>({
  entities,
  events,
  timeRange,
  granularity,
}: UseTimeGridLayoutProps<TEntity, TEvent>): {
  rows: Row<TEntity, TEvent>[]
  slots: TimeSlot[]
  range: TimeRange
  rowByEntityId: Map<string, Row<TEntity, TEvent>>
  rowIndexByEntityId: Map<string, number>
} {
  const extraHours = useUnsafeMemo(() => {
    if (granularity !== 'day') return 0
    return computeExtraHours(events, timeRange)
  }, [events, granularity, timeRange])

  const { slots, range } = useUnsafeMemo(
    () => buildTimeSlots({ timeRange, granularity, extraHours }),
    [timeRange, granularity, extraHours]
  )

  const groupedEvents = useUnsafeMemo(
    () => groupEventsByEntity({ entities, events }),
    [entities, events]
  )

  const rows = useUnsafeMemo(() => {
    return entities.map((entity) => {
      const rowEvents = groupedEvents.get(entity.id) ?? []
      const layouts = buildEventLayouts({ events: rowEvents, slots, timeRange: range })
      const overlaps = (left: EventLayout<TEvent>, right: EventLayout<TEvent>) => {
        if (granularity === 'day') {
          return left.event.start < right.event.end && left.event.end > right.event.start
        }

        return (
          left.span.startIndex <= right.span.endIndex && left.span.endIndex >= right.span.startIndex
        )
      }

      const { stacked } = stackEvents({ layouts, overlaps })
      const layoutById = new Map<string, EventLayout<TEvent>>()

      stacked.forEach((layout) => {
        layoutById.set(layout.event.id, layout)
      })

      return {
        entity,
        layouts: stacked,
        layoutById,
      }
    })
  }, [entities, groupedEvents, slots, range, granularity])

  const rowByEntityId = useUnsafeMemo(() => {
    return new Map(rows.map((row) => [row.entity.id, row]))
  }, [rows])

  const rowIndexByEntityId = useUnsafeMemo(() => {
    return new Map(rows.map((row, index) => [row.entity.id, index]))
  }, [rows])

  return {
    rows,
    slots,
    range,
    rowByEntityId,
    rowIndexByEntityId,
  }
}

export { useTimeGridLayout }
